import React, { useState, useRef, useEffect, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import Map from './Map';
import NearbyRecommend from './NearbyRecommend';
import './NearbyMap.css';
import { request } from '../utils/request'; // 导入request函数

const NearbyMap = () => {
  const [markers, setMarkers] = useState([]);
  const [resorts, setResorts] = useState([]);
  const [selectedResort, setSelectedResort] = useState(null);
  const [userLocation, setUserLocation] = useState({
    latitude: 39.9042,
    longitude: 116.4074
  });
  const [searchParams, setSearchParams] = useState({
    radius: 200,
    limit: 20,
    weights: {
      rating: 0,
      totalTrails: 20,
      verticalDrop: 20,
      lifts: 10,
      beginnerTrails: 0,
      intermediateTrails: 0,
      advancedTrails: 0,
      airportDistance: 0,
      avgSnowDays: 0,
      avgSnowDepth: 0,
      avgSnowfall: 0,
      cityDistance: 0,
      distance: 0,
      majorCityDistance: 0,
      price: 0,
      snowAmount: 0,
      snowDaysYearly: 0,
      snowProbability: 0,
      temperature: 0,
      trainDistance: 0,
      windSpeed: 0
    }
  });
  const [allowLocationUpdate, setAllowLocationUpdate] = useState(false);
  const mapRef = useRef();
  const userMarkerRef = useRef(null);

  // 修改 createUserMarker 函数
  const createUserMarker = useCallback((lngLat) => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    // 移除现有的用户标记
    if (userMarkerRef.current) {
      userMarkerRef.current.remove();
    }

    // 创建用户位置标记
    const el = document.createElement('div');
    el.className = 'user-marker'; // 使用 Map.jsx 的标记样式

    // 创建并保存标记
    userMarkerRef.current = new mapboxgl.Marker(el)
      .setLngLat(lngLat)
      .addTo(map);
  }, []);

  // 更新用户位置
  const handleLocationUpdate = useCallback((newLocation) => {
    setUserLocation(newLocation);
    setAllowLocationUpdate(false); // 位置更新后关闭允许更新状态
  }, []);

  // 监听地图加载和用户位置变化
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (map && userLocation) {
      createUserMarker([userLocation.longitude, userLocation.latitude]);
    }
  }, [userLocation, createUserMarker]);

  // 使用 useCallback 包装获取位置的函数
  const getUserLocation = useCallback(() => {
    setAllowLocationUpdate(true); // 点击重新定位时开启允许更新状态
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          handleLocationUpdate(newLocation);
        },
        () => {
          // 默认位置（北京）
          const defaultLocation = {
            latitude: 39.9042,
            longitude: 116.4074
          };
          handleLocationUpdate(defaultLocation);
        }
      );
    }
  }, [handleLocationUpdate]);

  // 组件加载时获取位置
  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  // 修改 fetchNearbyResorts 函数来处理地图标记
  const fetchNearbyResorts = useCallback(async () => {
    try {
      const data = await request({
        url: 'nearby',
        method: 'POST',
        data: {
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
          date: new Date().toISOString().split('T')[0],
          ...searchParams
        }
      });

      setResorts(data.data);
      
      // 将滑雪场数据转换为地图标记
      const newMarkers = data.data.map((resort, index) => ({
        id: resort.id,
        latitude: resort.latitude,
        longitude: resort.longitude,
        title: resort.name,
        content: `${resort.name}
排名: #${index + 1}
评分: ${Number(resort.score).toFixed(1)}
距离: ${resort.distance.toFixed(1)}km
雪道总长: ${resort.totalTrails}km
垂直落差: ${resort.verticalDrop}m`,
        resort: resort,
        rank: index + 1,
        score: resort.score
      }));
      
      setMarkers(newMarkers);
      
      // 确保有标记且地图已加载
      const map = mapRef.current?.getMap();
      if (map && newMarkers.length > 0) {
        // 创建包含所有标记和用户位置的边界
        const bounds = new mapboxgl.LngLatBounds();
        
        // 添加用户位置到边界
        bounds.extend([userLocation.longitude, userLocation.latitude]);
        
        // 添加所有标记到边界
        newMarkers.forEach(marker => {
          bounds.extend([marker.longitude, marker.latitude]);
        });

        // 调整地图视野以显示所有标记
        map.fitBounds(bounds, {
          padding: { top: 100, bottom: 100, left: 400, right: 100 }, // 左侧留出推荐面板的空间
          maxZoom: 10, // 限制最大缩放级别
          duration: 1000 // 添加动画效果
        });
      }
    } catch (error) {
      console.error('Error fetching nearby resorts:', error);
    }
  }, [userLocation, searchParams]);

  // 修改监听位置和参数变化的 useEffect
  useEffect(() => {
    fetchNearbyResorts();
  }, [userLocation, searchParams, fetchNearbyResorts]);

  // 修改 handleMarkersUpdate 函数来更新搜索参数并触发请求
  const handleMarkersUpdate = useCallback((newMarkers, newParams) => {
    if (newParams) {
      setSearchParams(prevParams => ({
        ...prevParams,
        ...newParams
      }));
    } else {
      setMarkers(newMarkers);
    }
  }, []);

  // 修改 handleResortSelect 函数，只设置 selectedResort
  const handleResortSelect = useCallback((resort) => {
    setSelectedResort(resort);
  }, []);

  const handleMapMove = () => {
    // 可以在这里处理地图移动事件
  };

  const handleMapLoad = useCallback(() => {
    if (userLocation) {
      createUserMarker([userLocation.longitude, userLocation.latitude]);
    }
  }, [userLocation, createUserMarker]);

  return (
    <div className="nearby-map-container">
      <NearbyRecommend
        resorts={resorts}
        onMarkersUpdate={handleMarkersUpdate}
        selectedResort={selectedResort}
        onResortSelect={handleResortSelect}
        userLocation={userLocation}
        onLocationUpdate={handleLocationUpdate}
        searchParams={searchParams}
        onGetLocation={getUserLocation}
      />
      
      <div className="map-wrapper">
        <Map
          ref={mapRef}
          markers={markers}
          selectedResort={selectedResort}
          onResortSelect={handleResortSelect}
          onMapMove={handleMapMove}
          onMapLoad={handleMapLoad}
          type="nearby"
          center={[userLocation.longitude, userLocation.latitude]}
          onLocationUpdate={allowLocationUpdate ? handleLocationUpdate : null}
        />
      </div>
    </div>
  );
};

export default NearbyMap;